import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'accounting:activeCompanyId';
const listeners = new Set<() => void>();

/** Sentinel value used when the consolidated (all-flagged-companies) view is active. */
export const CONSOLIDATED = '__consolidated__';

function read(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

export function setActiveCompanyId(id: string | null): void {
  if (id === null) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, id);
  }
  listeners.forEach((cb) => cb());
}

/** Raw selection: a company UUID, the CONSOLIDATED sentinel, or null. */
export function useActiveSelection(): string | null {
  return useSyncExternalStore(subscribe, read, () => null);
}

/**
 * The active single company UUID, or null when nothing (or the consolidated
 * view) is selected. Per-company editing pages use this so they degrade
 * gracefully to their empty state under the consolidated view.
 */
export function useActiveCompanyId(): string | null {
  const sel = useActiveSelection();
  return sel === CONSOLIDATED ? null : sel;
}

export function useIsConsolidated(): boolean {
  return useActiveSelection() === CONSOLIDATED;
}
